/**
 * -6차 「지급」·「회수」탭이 함께 쓰는 바탕 — 회원칸 · 검색 목록 · 표기 유틸.
 *
 * 두 탭은 하는 일이 반대라 **화면·모듈을 나눴다** (Give.js / Recall.js). 다만 둘 다
 *   · 위: 회원을 골라(`이름#번호`) 그 회원에게 무엇을 한다
 *   · 아래: 전체 이력을 회원·기간·사유로 검색한다
 * 는 같은 모양이라, 그 뼈대만 여기 둔다. 탭 고유의 동작은 이 파일에 넣지 않는다.
 *
 * 검색칸 규약(CLAUDE.md '검색 인풋에 숨은 상태를 두지 않는다') — 인풋에 보이는 텍스트가 곧 기준이다.
 *   회원칸  `이름#번호` 한 명 / `이름` 그 이름으로 시작하는 회원 전원 (서버: MsixAdminListService)
 */
import Util from '../../../Common/Util';

export default class MsixPaneBase {
    /**
     * @param {string} prefix  블레이드 id 접두어 (`mgv` 지급 / `mrc` 회수)
     * @param {object} opts    onChange: 원장이 바뀌었을 때 페이지가 KPI 등을 다시 읽게 하는 콜백
     */
    constructor(prefix, opts = {}) {
        this.p        = prefix;
        this.onChange = opts.onChange || null;
        this.mbNo     = 0;
        this.board    = null;   // 마지막으로 불러온 회원 판
        this._opened  = false;
    }

    /** `#${prefix}-${name}` */
    el(name) { return $(`#${this.p}-${name}`); }

    /** 이 탭 조각이 페이지에 있나 — 없으면 start() 가 아무것도 안 한다 */
    exists() { return this.el('mem').length > 0; }

    // ─────────────────────────────────────────────── 회원칸

    /** `이름#번호` 에서 번호만 — 인풋에 보이는 값이 곧 대상이다 */
    pickedNo($in) {
        const m = String($in.val() || '').match(/#(\d+)\s*$/);
        return m ? Util.int(m[1]) : 0;
    }

    bindMemberAc($in, onPick) {
        if (!$in.length) return;

        $j.F.Admin.Util.autocomplete($in, {
            url: '/adm/ajax/member/find/autoComplete',
            render: (r) => {
                const tel = r.mem_phone ? ` <span class="text-muted">${this.esc(r.mem_phone)}</span>` : '';
                return `<b>#${Util.int(r.num)}</b> ${this.esc(r.mem_name || '')}${tel}`;
            },
            pick: (r) => {
                $in.val(`${r.mem_name || ''}#${Util.int(r.num)}`);
                if (onPick) onPick();
            },
        });
    }

    /**
     * 위쪽 회원칸 — 고르는 즉시 판을 부른다. `이름#번호` 붙여넣기는 change(Enter·포커스아웃)가 받는다.
     * 고른 직후 blur 가 change 를 한 번 더 흘리므로 지금 보고 있는 회원이면 건너뛴다.
     */
    bindTargetMember() {
        const $in = this.el('mem');
        this.bindMemberAc($in, () => this.loadBoard());
        $in.on('change', () => {
            if (this.pickedNo($in) !== this.mbNo) this.loadBoard();
        });
    }

    /** 다른 곳(목록 행·주소)에서 회원을 넘겨받아 판을 연다 */
    openMember(name, mbNo) {
        this.el('mem').val(`${name || ''}#${Util.int(mbNo)}`);
        this.loadBoard();
        const el = this.el('mem').get(0);
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    async loadBoard() {
        const mbNo = this.pickedNo(this.el('mem'));
        if (!mbNo) {
            this.mbNo = 0; this.board = null;
            this.el('board').html(this.hint('회원을 목록에서 골라주세요. (이름만 입력하면 불러오지 않습니다)'));
            return;
        }

        // 응답을 기다리기 전에 잡아둔다 — 자동완성 클릭이 곧 blur 라 change 가 뒤따라 온다
        this.mbNo = mbNo;

        try {
            const r = await $.ajax({ url: '/adm/ajax/msix/member', type: 'get', data: { mb_no: mbNo }, dataType: 'json' });
            if (!r.ok) {
                this.mbNo = 0;   // 실패했으면 다시 시도할 수 있어야 한다
                $j.F.Admin.Util.tsalert('error', r.message || '회원을 불러오지 못했습니다.');
                return;
            }
            this.board = r;
            this.el('board').html(this.boardHtml(r));
            this.afterBoard();
        } catch (e) {
            this.mbNo = 0;
            console.error('-6차 회원 판 로드 실패', e);
            this.el('board').html(this.hint('회원 정보를 불러오지 못했습니다.'));
        }
    }

    /** 탭이 그린다 */
    boardHtml() { return ''; }
    afterBoard() {}

    /** 고른 회원 한 줄 — 누구를, 지금 얼마나 */
    whoHtml(r, tail = '') {
        const m      = r.member || {};
        const sum    = r.sum || {};
        const active = Util.int(sum.active_cnt);

        return `
        <div class="mg-who">
            <b>${$j.F.Admin.Member.Pop.cell(Util.int(m.num), m.mem_name)}</b>
            ${m.mem_phone ? `<span class="text-muted">${this.esc(m.mem_phone)}</span>` : ''}
            ${m.sido ? `<span class="text-muted">${this.esc(m.sido)}</span>` : ''}
            <span class="ms-badge ${active ? 'is-live' : ''}">보유 ${active}건</span>
            <span class="ms-badge">점수 ${this.n(sum.active_pt)}원</span>
            ${tail}
        </div>`;
    }

    // ─────────────────────────────────────────────── 검색 목록

    /** 검색칸 값 — 서버가 받는 이름 그대로 (mem · from · to · q · state) */
    filterData(d) {
        d.mem  = $.trim(this.el('f-mem').val());
        d.from = this.el('f-from').val() || '';
        d.to   = this.el('f-to').val() || '';
        d.q    = $.trim(this.el('f-q').val());
        if (this.el('f-state').length) d.state = this.el('f-state').val();
    }

    /**
     * 검색 툴바 — [검색] · Enter · 셀렉트/날짜 변경이 다시 읽고, [초기화]는 칸을 비운다.
     * 회원칸은 자동완성으로 고르면 바로 검색한다.
     */
    bindFilter(reload) {
        const f = (n) => this.el(`f-${n}`);

        this.bindMemberAc(f('mem'), reload);
        this.el('f-search').on('click', reload);
        f('mem').add(f('q')).on('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); reload(); } });
        f('from').add(f('to')).add(f('state')).on('change', reload);
        this.el('f-reset').on('click', () => {
            f('mem').add(f('q')).add(f('from')).add(f('to')).val('');
            f('state').each((i, el) => { el.selectedIndex = 0; });
            reload();
        });
    }

    dataTable(selector, url, columns, extra = {}) {
        return new DataTable(selector, Object.assign({
            autoWidth: false,
            processing: true, serverSide: true, ordering: false,
            pageLength: 20, lengthChange: false, searching: false,
            ajax: { url, type: 'post', data: (d) => this.filterData(d) },
            columns,
            language: {
                emptyTable: '데이터가 없습니다.', loadingRecords: '로딩 중...', processing: '불러오는 중...',
                zeroRecords: '일치하는 결과가 없습니다.', info: '_START_–_END_ / _TOTAL_건',
                infoEmpty: '0건', infoFiltered: '',
                paginate: { previous: "<i class='fa fa-chevron-left'></i>", next: "<i class='fa fa-chevron-right'></i>" },
            },
        }, extra));
    }

    // ─────────────────────────────────────────────── 표기

    memCell(row) { return $j.F.Admin.Member.Pop.cell(Util.int(row.mb_no), row.mem_name); }

    /** 목록 행 → 위 회원칸으로 여는 작은 버튼 */
    openBtn(row) {
        return `<button type="button" class="btn btn-link btn-sm p-0 mg-open" style="font-size:11px"
                        data-mb="${Util.int(row.mb_no)}" data-name="${this.esc(row.mem_name || '')}">위에서 열기</button>`;
    }

    hint(s)   { return `<div class="text-muted py-3" style="font-size:13px">${this.esc(s)}</div>`; }
    n(v)      { return Number(v ?? 0).toLocaleString(); }
    nowrap(s) {
        const v = this.esc(s);
        return v ? `<span style="white-space:nowrap">${v}</span>` : '';
    }
    esc(s) {
        return String(s ?? '').replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    }
}
